// ABOUTME: Highlighted section for important information in tooltips
// ABOUTME: Supports info, warning, success, and error variants

import { memo, type ReactNode } from "react"
import { AlertTriangleIcon, InfoCircleIcon } from "@shared/icons"

type HighlightType = "info" | "warning" | "success" | "error"

interface TooltipHighlightProps {
  type?: HighlightType
  icon?: ReactNode
  children: ReactNode
  className?: string
}

const highlightStyles: Record<HighlightType, { bg: string; text: string; border: string }> = {
  info: {
    bg: "bg-blue-500/10",
    text: "text-blue-300",
    border: "border-blue-500/30",
  },
  warning: {
    bg: "bg-amber-500/10",
    text: "text-amber-300",
    border: "border-amber-500/30",
  },
  success: {
    bg: "bg-green-500/10",
    text: "text-green-300",
    border: "border-green-500/30",
  },
  error: {
    bg: "bg-red-500/10",
    text: "text-red-300",
    border: "border-red-500/30",
  },
}

const defaultIcons: Record<HighlightType, ReactNode> = {
  info: <InfoCircleIcon className="w-3.5 h-3.5" />,
  warning: <AlertTriangleIcon className="w-3.5 h-3.5" />,
  success: null,
  error: <AlertTriangleIcon className="w-3.5 h-3.5" />,
}

export const TooltipHighlight = memo(({ type = "info", icon, children, className = "" }: TooltipHighlightProps) => {
  const styles = highlightStyles[type]
  const displayIcon = icon ?? defaultIcons[type]

  return (
    <div
      className={`
        mt-2 p-2 rounded-md border
        ${styles.bg} ${styles.border}
        ${className}
      `}
    >
      <div className={`flex items-start gap-2 text-[11px] leading-[14px] ${styles.text}`}>
        {displayIcon && <span className="flex-shrink-0 mt-0.5">{displayIcon}</span>}
        <span className="whitespace-normal">{children}</span>
      </div>
    </div>
  )
})

TooltipHighlight.displayName = "TooltipHighlight"
